import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Card, PillButton, Input, SegmentedControl, EmptyState, IconCircle, FeatureBanner } from '../../ui';
import { ReactComponent as OfficeIcon } from '../../assets/icons/office-building.svg';
import { ReactComponent as UsersIcon } from '../../assets/icons/users.svg';
import { ReactComponent as LayersIcon } from '../../assets/icons/layers.svg';
import { ReactComponent as ShieldCheckIcon } from '../../assets/icons/shield-check.svg';
import { ReactComponent as FolderLockIcon } from '../../assets/icons/folder-lock.svg';
import { ReactComponent as SlidersIcon } from '../../assets/icons/sliders-vertical.svg';
import { ReactComponent as PaletteIcon } from '../../assets/icons/palette.svg';
import { ReactComponent as CreditCardIcon } from '../../assets/icons/credit-card.svg';
import { ReactComponent as ListIcon } from '../../assets/icons/list.svg';
import { ReactComponent as ChevronLeftIcon } from '../../assets/icons/chevron-left.svg';
import { ReactComponent as AlertCircleIcon } from '../../assets/icons/alert-circle.svg';
import './AdminLayout.css';
import { apiErrorMessage } from '../../utils/apiError';

/*
 * CONTRATO DEL AREA ADMIN
 * -----------------------
 * Este layout resuelve la organización activa (por :orgId en la URL) y la
 * expone a las pestañas via useOutletContext(): { org, rolePermissions,
 * has(permission), seatsUsed, reloadOrg, orgId }.
 * Para añadir una pestaña nueva: componente en pages/admin/<Nombre>.js,
 * entrada en TABS de aquí abajo (key = segmento de ruta) y su <Route> en
 * App.js dentro de <Route path="admin" element={<AdminLayout />}>.
 */
const TABS = [
  { key: 'members', icon: UsersIcon, perm: null },
  { key: 'departments', icon: LayersIcon, perm: null },
  { key: 'roles', icon: ShieldCheckIcon, perm: null },
  { key: 'vaults', icon: FolderLockIcon, perm: null },
  { key: 'policies', icon: SlidersIcon, perm: 'policies.manage' },
  { key: 'branding', icon: PaletteIcon, perm: 'branding.manage' },
  { key: 'billing', icon: CreditCardIcon, perm: 'billing.manage' },
  { key: 'audit', icon: ListIcon, perm: 'audit.read' },
];

function AdminLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();

  const [orgs, setOrgs] = useState(null); // null = cargando
  const [org, setOrg] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [seatsUsed, setSeatsUsed] = useState(0);
  const [orgError, setOrgError] = useState('');
  const [newName, setNewName] = useState('');
  // ?plan=team|business (venido de "crear cuenta" en Login.js): preselecciona
  // el plan del asistente en vez del team por defecto.
  const [newPlan, setNewPlan] = useState(searchParams.get('plan') === 'business' ? 'business' : 'team');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  // La org exige PIN o contrasena maestra y esta cuenta no lo tiene: cualquier
  // llamada de la org responde 403 `org_requires_second_factor`.
  const [needsFactor, setNeedsFactor] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setNeedsFactor(false);
    const id = api.interceptors.response.use(undefined, (err) => {
      if (err.response?.status === 403 && err.response.data?.code === 'org_requires_second_factor') {
        setNeedsFactor(true);
      }
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, [orgId]);

  const loadOrgs = useCallback(() => {
    api.get('/org/mine')
      .then((res) => setOrgs(res.data?.organizations || []))
      .catch(() => setOrgs([]));
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  // Si no hay orgId en la URL y el usuario pertenece a una sola org, entra directo.
  useEffect(() => {
    if (!orgId && orgs && orgs.length === 1) {
      navigate(`/admin/${orgs[0].organization.id}/members`, { replace: true });
    }
  }, [orgId, orgs, navigate]);

  const reloadOrg = useCallback(() => {
    if (!orgId) return;
    setOrgError('');
    api.get(`/org/${orgId}`)
      .then((res) => {
        setOrg(res.data.organization);
        setRolePermissions(res.data.rolePermissions || []);
        setSeatsUsed(res.data.seatsUsed || 0);
      })
      .catch((err) => setOrgError(apiErrorMessage(err, t, 'errors.generic')));
  }, [orgId, t]);

  useEffect(() => { reloadOrg(); }, [reloadOrg]);

  const has = useCallback((perm) => rolePermissions.includes(perm), [rolePermissions]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setCreateError('');
    try {
      const res = await api.post('/org', { name: newName.trim(), plan: newPlan });
      const createdId = res.data.organization.id;
      setNewName('');
      loadOrgs();
      navigate(`/admin/${createdId}/members`);
    } catch (err) {
      setCreateError(apiErrorMessage(err, t, 'errors.generic'));
    } finally {
      setCreating(false);
    }
  };

  // ---------- Sin orgId en la URL: selector / creación ----------
  if (!orgId) {
    return (
      <div className="sgc-admin sgc-admin--onboarding">
        <h1 className="sgc-admin__title">{t('admin.title')}</h1>

        {orgs === null ? (
          <p>{t('common.loading')}</p>
        ) : orgs.length > 0 && (
          <Card className="sgc-admin__org-list">
            {orgs.map((o) => (
              <button
                key={o.organization.id}
                type="button"
                className="sgc-admin__org-item"
                onClick={() => navigate(`/admin/${o.organization.id}/members`)}
              >
                <IconCircle icon={OfficeIcon} tone="surface" size={36} />
                <span className="sgc-admin__org-item-text">
                  <strong>{o.organization.name}</strong>
                  <span className="sgc-admin__muted">{t(`admin.plans.${o.organization.plan}`, o.organization.plan)} · {o.role.name}</span>
                </span>
              </button>
            ))}
          </Card>
        )}

        <Card className="sgc-admin__create-card">
          <h2 className="sgc-admin__section-title">{t('admin.createOrg.title')}</h2>
          <form onSubmit={handleCreate} className="sgc-admin__create-form">
            <Input
              label={t('admin.createOrg.nameLabel')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
            />
            <SegmentedControl
              options={[
                { value: 'team', label: t('admin.plans.team') },
                { value: 'business', label: t('admin.plans.business') },
              ]}
              value={newPlan}
              onChange={setNewPlan}
            />
            {createError && <p className="sgc-admin__error">{createError}</p>}
            <PillButton type="submit" disabled={creating || !newName.trim()}>
              {creating ? t('common.loading') : t('admin.createOrg.submit')}
            </PillButton>
          </form>
        </Card>
      </div>
    );
  }

  // ---------- Con orgId: cabecera + pestañas + outlet ----------
  if (orgError) {
    return (
      <div className="sgc-admin">
        <EmptyState
          icon={OfficeIcon}
          title={t('admin.errorTitle')}
          description={orgError}
          actionLabel={t('admin.backToSelector')}
          onAction={() => navigate('/admin')}
        />
      </div>
    );
  }

  if (!org) {
    return <div className="sgc-admin"><p>{t('common.loading')}</p></div>;
  }

  const visibleTabs = TABS.filter((tab) => !tab.perm || has(tab.perm));

  return (
    <div className="sgc-admin">
      <div className="sgc-admin__header">
        <button type="button" className="sgc-admin__back" onClick={() => navigate('/admin')} title={t('admin.backToSelector')}>
          <ChevronLeftIcon width={18} height={18} aria-hidden="true" />
        </button>
        <div>
          <h1 className="sgc-admin__title">{org.name}</h1>
          <p className="sgc-admin__muted">{t(`admin.plans.${org.plan}`, org.plan)} · {t(`admin.status.${org.status}`, org.status)}</p>
        </div>
      </div>

      <nav className="sgc-segmented sgc-admin__tabs" role="tablist">
        {visibleTabs.map((tab) => (
          <NavLink
            key={tab.key}
            to={`/admin/${orgId}/${tab.key}`}
            className={({ isActive }) => `sgc-segmented__item ${isActive ? 'sgc-segmented__item--active' : ''}`}
          >
            <tab.icon width={16} height={16} aria-hidden="true" />
            {t(`admin.tabs.${tab.key}`)}
          </NavLink>
        ))}
      </nav>

      <div className="sgc-admin__content">
        {needsFactor && (
          <FeatureBanner
            icon={AlertCircleIcon}
            tone="warn"
            title={t('admin.secondFactor.notice')}
            subtitle={t('admin.secondFactor.cta')}
            onClick={() => navigate('/account/security')}
          />
        )}
        {/* Con el aviso activo solo se deja abrir Politicas (usa GET /org/:id); el resto de pestanas darian 403. */}
        {(!needsFactor || location.pathname.endsWith('/policies')) && (
          <Outlet context={{ org, rolePermissions, has, seatsUsed, reloadOrg, orgId }} />
        )}
      </div>
    </div>
  );
}

export default AdminLayout;
